
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { validateProfileUrl } from "@/utils/url-validation";

interface CustomLink {
  label: string;
  url: string;
  thumbnail?: string;
}

interface CustomLinksFieldProps {
  form: UseFormReturn<any>;
}

// Purple gradient that matches Lovable's theme
const DEFAULT_GRADIENT = "linear-gradient(102.3deg, rgba(147,39,143,1) 5.9%, rgba(234,172,232,1) 64%, rgba(246,219,245,1) 89%)";

const CustomLinksField: React.FC<CustomLinksFieldProps> = ({ form }) => {
  const customLinks = form.watch("custom_links") || [];

  const addLink = () => {
    if (customLinks.length >= 5) {
      return; // Maximum 5 links allowed
    }
    const currentLinks = form.getValues("custom_links") || [];
    form.setValue("custom_links", [
      ...currentLinks,
      { label: "", url: "", thumbnail: DEFAULT_GRADIENT },
    ]);
  };

  const removeLink = (index: number) => {
    const currentLinks = form.getValues("custom_links") || [];
    form.setValue(
      "custom_links",
      currentLinks.filter((_: any, i: number) => i !== index)
    );
  };

  const handleUrlChange = (index: number, url: string, link: CustomLink) => {
    const { isValid, sanitizedUrl } = validateProfileUrl(url);
    console.log('URL validation result:', { isValid, sanitizedUrl, originalUrl: url });
    
    const newLinks = [...customLinks];
    newLinks[index] = { 
      ...link, 
      url: isValid ? sanitizedUrl : url 
    };
    form.setValue("custom_links", newLinks);
  };

  return (
    <FormField
      control={form.control}
      name="custom_links"
      render={() => (
        <FormItem className="space-y-4">
          <FormLabel className="text-base font-medium">Links (Max 5)</FormLabel>
          <div className="space-y-4">
            {customLinks.map((link: CustomLink, index: number) => (
              <div key={index} className="flex flex-col md:flex-row gap-3 bg-[#111418] p-3 rounded-lg border border-white/10">
                <div className="flex flex-col space-y-2 flex-1">
                  <FormLabel className="text-sm text-white/70">Link Label</FormLabel>
                  <Input
                    placeholder="Label (e.g. Follow me on Twitter)"
                    value={link.label || ''}
                    onChange={(e) => {
                      const newLinks = [...customLinks];
                      newLinks[index] = { ...link, label: e.target.value };
                      form.setValue("custom_links", newLinks);
                    }}
                    className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10"
                  />
                </div>
                <div className="flex flex-col space-y-2 flex-1">
                  <FormLabel className="text-sm text-white/70">URL</FormLabel>
                  <Input
                    placeholder="https://..."
                    value={link.url || ''}
                    onChange={(e) => handleUrlChange(index, e.target.value, link)}
                    className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10"
                  />
                </div>
                <div className="flex items-end md:self-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeLink(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 hover:border-red-900/30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {customLinks.length < 5 && (
            <Button
              type="button"
              variant="outline"
              className="w-full mt-2 border-dashed border-white/30 hover:border-primary/50 hover:bg-primary/10"
              onClick={addLink}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </Button>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default CustomLinksField;
